import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  KeyRound, LogOut, ShieldAlert, ShieldCheck, Eye, EyeOff, Plus,
  Clock, Smartphone, MonitorSmartphone, Loader2, X, LinkIcon, Unlink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import ReauthDialog, { isReauthError, reauthMethodsFromError } from "@/components/ReauthDialog";

const PROVIDER_LABEL = { google: "Google", apple: "Apple" };

export default function SecurityTab() {
  const { collector, logout, refresh } = useCollectorAuth();
  const navigate = useNavigate();
  const [methods, setMethods] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [events, setEvents] = useState(null);
  const [reauth, setReauth] = useState({
    open: false, methods: ["password"], title: "", copy: "", next: null,
  });

  const loadAll = useCallback(async () => {
    try {
      const [m, s, e] = await Promise.all([
        api.get("/security/methods"),
        api.get("/security/sessions"),
        api.get("/security/events?limit=20"),
      ]);
      setMethods(m.data.methods);
      setSessions(s.data.sessions || []);
      setEvents(e.data.events || []);
    } catch (err) {
      toast.error("Couldn't load your security details.");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  /** Run a sensitive action; on reauth-required error, open the dialog and
   * retry after successful reauth. */
  const runProtected = async (fn, dialog) => {
    try { await fn(); }
    catch (err) {
      if (isReauthError(err)) {
        setReauth({
          open: true,
          methods: reauthMethodsFromError(err),
          title: dialog?.title || "Confirm your identity",
          copy: dialog?.copy || "For your security, please confirm your identity to continue.",
          next: fn,
        });
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    }
  };

  const reauthSuccess = async () => {
    const next = reauth.next;
    setReauth({ ...reauth, open: false, next: null });
    if (next) {
      try { await next(); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    }
    await loadAll();
  };

  return (
    <div className="space-y-10" data-testid="security-tab">
      <ReauthDialog
        open={reauth.open}
        methods={reauth.methods}
        title={reauth.title}
        copy={reauth.copy}
        onClose={() => setReauth((s) => ({ ...s, open: false, next: null }))}
        onSuccess={reauthSuccess}
      />

      {/* ============ LOGIN METHODS ============ */}
      <Section title="Login methods"
                subtitle="Keep at least two ways to sign in so you never lose access.">
        <LoginMethods
          methods={methods}
          collector={collector}
          runProtected={runProtected}
          onChanged={loadAll}
        />
      </Section>

      {/* ============ ACTIVE SESSIONS ============ */}
      <Section title="Active sessions"
                subtitle="Sign out any device you no longer recognise.">
        <SessionsList
          sessions={sessions}
          runProtected={runProtected}
          onChanged={loadAll}
          onCurrentRevoked={async () => { await logout(); navigate("/login", { replace: true }); }}
        />
      </Section>

      {/* ============ RECENT ACTIVITY ============ */}
      <Section title="Recent security activity"
                subtitle="A safe, high-level log of what happened on your account.">
        <EventsList events={events} />
      </Section>

      {/* ============ ACCOUNT STATUS ============ */}
      <Section title="Account status" subtitle="Contact support to deactivate or delete your account.">
        <div className="rounded-2xl border-2 border-[var(--line)] bg-white p-4 flex items-start gap-3"
              data-testid="security-account-status">
          <ShieldAlert className="text-[var(--panini-red)] shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-bold text-[var(--ink)] text-sm">Account is {collector?.account_status || "active"}</div>
            <div className="text-sm text-[var(--smoke)] mt-1">
              Reach us at{" "}
              <a href="mailto:support@collectorup.com" className="underline font-bold">support@collectorup.com</a>{" "}
              if you need to change this.
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ============================================================================
// Login methods
// ============================================================================
function LoginMethods({ methods, collector, runProtected, onChanged }) {
  const [showChangePw, setShowChangePw] = useState(false);
  const [showSetPw, setShowSetPw] = useState(false);

  if (!methods) return <div className="gm-skel h-24 rounded-2xl" data-testid="methods-loading" />;

  const hasPw = methods.password.status === "configured";

  const connect = (provider) => {
    // Provider link start requires recent-auth. If it 401s with reauth_required,
    // the API layer will bubble; here we redirect to the OAuth start URL. The
    // browser follows the 302; if it returns 401, we catch and open dialog.
    const url = `${API}/auth/oauth/${provider}/start?mode=link&return_to=${encodeURIComponent("/dashboard?tab=security")}`;
    runProtected(async () => {
      // Preflight the endpoint to catch reauth_required before a full page load.
      // The start endpoint returns 302 to Google/Apple on success; we detect
      // reauth_required from 401 first.
      const r = await api.get(`/auth/oauth/${provider}/start?mode=link&return_to=${encodeURIComponent("/dashboard?tab=security")}`,
                                { maxRedirects: 0, validateStatus: (s) => s < 500 });
      if (r.status === 302 || r.status === 200) {
        window.location.href = url;
      } else if (r.status === 401 || r.status === 403 || r.status === 404) {
        // If it's reauth_required error interceptor will throw; other 4xx surface below.
        const detail = r.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "reauth_required") {
          throw { response: { status: 401, data: { detail } } };
        }
        throw new Error(formatApiError(detail) || `Cannot connect ${provider}.`);
      }
    }, {
      title: `Confirm your identity to connect ${PROVIDER_LABEL[provider]}`,
      copy: "Connecting a login method is a sensitive action. Please confirm to continue.",
    });
  };

  const disconnect = (provider) => runProtected(async () => {
    if (!window.confirm(`Disconnect ${PROVIDER_LABEL[provider]} from this account?`)) return;
    await api.post(`/security/providers/${provider}/disconnect`);
    toast.success(`${PROVIDER_LABEL[provider]} disconnected.`);
    onChanged?.();
  }, {
    title: `Confirm to disconnect ${PROVIDER_LABEL[provider]}`,
    copy: "You'll lose the ability to sign in with this provider. Confirm to continue.",
  });

  return (
    <div className="space-y-3" data-testid="login-methods">
      <MethodRow
        name="Email and password"
        statusLabel={hasPw ? "Configured" : "Not set"}
        statusTone={hasPw ? "ok" : "warn"}
        icon={<KeyRound size={18} />}
        testid="method-password"
        action={
          hasPw ? (
            <button type="button" onClick={() => setShowChangePw(true)}
                     data-testid="method-password-change"
                     className="btn-ghost text-xs">
              Change password
            </button>
          ) : (
            <button type="button" onClick={() => setShowSetPw(true)}
                     data-testid="method-password-set"
                     className="btn-red text-xs">
              <Plus size={14} /> Set password
            </button>
          )
        }
      />

      {["google", "apple"].map((p) => {
        const m = methods[p];
        const cfg = statusCfgFor(m.status);
        const linked = m.status === "connected" || m.status === "connected_disabled";
        return (
          <MethodRow
            key={p}
            name={PROVIDER_LABEL[p]}
            statusLabel={cfg.label}
            statusTone={cfg.tone}
            icon={<LinkIcon size={18} />}
            testid={`method-${p}`}
            action={
              linked ? (
                <button
                  type="button"
                  onClick={() => disconnect(p)}
                  data-testid={`method-${p}-disconnect`}
                  className="btn-ghost text-xs text-[var(--panini-red)]"
                >
                  <Unlink size={14} /> Disconnect
                </button>
              ) : m.status === "available" ? (
                <button
                  type="button"
                  onClick={() => connect(p)}
                  data-testid={`method-${p}-connect`}
                  className="btn-red text-xs"
                >
                  <Plus size={14} /> Connect
                </button>
              ) : (
                <span
                  data-testid={`method-${p}-unavailable`}
                  className="text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-[var(--panini-yellow-soft)] text-[var(--ink)]"
                >
                  Coming later
                </span>
              )
            }
          />
        );
      })}

      {showChangePw && (
        <ChangePasswordCard runProtected={runProtected} onDone={() => { setShowChangePw(false); onChanged?.(); }} />
      )}
      {showSetPw && (
        <SetPasswordCard runProtected={runProtected} onDone={() => { setShowSetPw(false); onChanged?.(); }} />
      )}
    </div>
  );
}

function statusCfgFor(status) {
  switch (status) {
    case "connected":          return { label: "Connected",           tone: "ok" };
    case "connected_disabled": return { label: "Connected · Temporarily unavailable", tone: "warn" };
    case "available":          return { label: "Available",           tone: "info" };
    default:                     return { label: "Not available",        tone: "muted" };
  }
}

function MethodRow({ name, statusLabel, statusTone, icon, action, testid }) {
  const toneCls = {
    ok:   "bg-[color:var(--field-green-soft)] text-[color:var(--field-green-dark)]",
    warn: "bg-[var(--panini-yellow-soft)] text-[var(--ink)]",
    info: "bg-white border border-[var(--line)] text-[var(--ink)]",
    muted:"bg-[var(--line)] text-[var(--smoke)]",
  }[statusTone] || "bg-[var(--line)] text-[var(--smoke)]";
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 shadow-[3px_3px_0_var(--ink)]"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className="w-10 h-10 rounded-full grid place-items-center bg-[var(--panini-yellow-soft)] border border-[var(--ink)] shrink-0">
          {icon}
        </span>
        <div className="min-w-0">
          <div className="font-bold text-[var(--ink)] text-sm">{name}</div>
          <span
            className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-widest ${toneCls}`}
            data-testid={`${testid}-status`}
          >
            {statusLabel}
          </span>
        </div>
      </div>
      {action}
    </div>
  );
}

// ============================================================================
// Change password card
// ============================================================================
function ChangePasswordCard({ runProtected, onDone }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    await runProtected(async () => {
      await api.post("/collector-auth/password-change", {
        current_password: pw.current, new_password: pw.next,
      });
      toast.success("Password updated. Other sessions have been signed out.");
      onDone?.();
    }, { title: "Confirm your identity to change your password" });
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-4 space-y-3"
           data-testid="change-password-form">
      <div className="flex items-center justify-between">
        <div className="font-bold">Change password</div>
        <button type="button" onClick={onDone} aria-label="Close" className="text-[var(--smoke)]">
          <X size={18} />
        </button>
      </div>
      <PwField label="Current password" value={pw.current} show={show} onToggle={() => setShow((v) => !v)}
                onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))}
                testid="cpw-current" />
      <PwField label="New password" value={pw.next} show={show} onToggle={() => setShow((v) => !v)}
                onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
                testid="cpw-new" />
      <PwField label="Confirm new password" value={pw.confirm} show={show} onToggle={() => setShow((v) => !v)}
                onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
                testid="cpw-confirm" />
      <button type="submit" disabled={busy} data-testid="cpw-submit"
               className="btn-red text-sm disabled:opacity-60 w-full sm:w-auto justify-center">
        {busy ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}

// ============================================================================
// Set password (social-only) card
// ============================================================================
function SetPasswordCard({ runProtected, onDone }) {
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    await runProtected(async () => {
      await api.post("/security/set-password", {
        new_password: pw.next, confirm: pw.confirm,
      });
      toast.success("Password created.");
      onDone?.();
    }, { title: "Confirm your identity to create a password" });
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-4 space-y-3"
           data-testid="set-password-form">
      <div className="flex items-center justify-between">
        <div className="font-bold">Add a password</div>
        <button type="button" onClick={onDone} aria-label="Close" className="text-[var(--smoke)]">
          <X size={18} />
        </button>
      </div>
      <p className="text-xs text-[var(--smoke)]">
        Adds email/password sign-in to your account. Your social providers stay connected.
      </p>
      <PwField label="New password" value={pw.next} show={show} onToggle={() => setShow((v) => !v)}
                onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))}
                testid="spw-new" />
      <PwField label="Confirm new password" value={pw.confirm} show={show} onToggle={() => setShow((v) => !v)}
                onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))}
                testid="spw-confirm" />
      <button type="submit" disabled={busy} data-testid="spw-submit"
               className="btn-red text-sm disabled:opacity-60 w-full sm:w-auto justify-center">
        {busy ? "Creating…" : "Create password"}
      </button>
    </form>
  );
}

// ============================================================================
// Sessions list
// ============================================================================
function SessionsList({ sessions, runProtected, onChanged, onCurrentRevoked }) {
  if (!sessions) return <div className="gm-skel h-24 rounded-2xl" data-testid="sessions-loading" />;

  const revoke = (sid, isCurrent) => runProtected(async () => {
    if (isCurrent && !window.confirm("This is your current session — you'll be signed out. Continue?")) return;
    const r = await api.post(`/security/sessions/${sid}/revoke`);
    toast.success(isCurrent ? "You've been signed out." : "Session revoked.");
    if (r.data?.is_current) { onCurrentRevoked?.(); return; }
    onChanged?.();
  }, { title: "Confirm your identity to revoke this session" });

  const revokeOthers = () => runProtected(async () => {
    if (!window.confirm("Sign out every other device? Your current tab stays signed in.")) return;
    const r = await api.post("/security/sessions/revoke-others");
    toast.success(`Signed out ${r.data?.revoked_count ?? 0} other devices.`);
    onChanged?.();
  }, { title: "Confirm your identity to sign out other devices" });

  return (
    <div className="space-y-3" data-testid="sessions-list">
      {sessions.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] bg-white p-4 text-sm text-[var(--smoke)]"
              data-testid="sessions-empty">
          No active sessions.
        </div>
      ) : sessions.map((s) => (
        <SessionRow key={s.id} s={s} onRevoke={() => revoke(s.id, s.is_current)} />
      ))}

      {sessions.filter((s) => !s.is_current).length > 0 && (
        <div className="pt-2">
          <button
            type="button"
            onClick={revokeOthers}
            data-testid="sessions-revoke-others"
            className="btn-ghost text-xs"
          >
            <LogOut size={13} /> Sign out other devices
          </button>
        </div>
      )}
    </div>
  );
}

function SessionRow({ s, onRevoke }) {
  const method = { password: "Email + password", google: "Google", apple: "Apple", reauth: "Reauthentication" }[s.method] || s.method;
  return (
    <div
      data-testid={`session-row-${s.id}`}
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 flex flex-col md:flex-row md:items-center gap-3 shadow-[3px_3px_0_var(--ink)]"
    >
      <div className="w-10 h-10 rounded-full grid place-items-center bg-[var(--panini-yellow-soft)] border border-[var(--ink)] shrink-0">
        {/iphone|ipad|android/i.test(s.user_agent_summary || "")
          ? <Smartphone size={18} />
          : <MonitorSmartphone size={18} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-[var(--ink)] text-sm">
            {s.user_agent_summary || "Unknown device"}
          </div>
          {s.is_current && (
            <span
              data-testid={`session-current-${s.id}`}
              className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--field-green-soft)] text-[color:var(--field-green-dark)]"
            >
              This device
            </span>
          )}
        </div>
        <div className="text-[11px] text-[var(--smoke)] mt-1">
          {method} · signed in {timeAgo(s.authenticated_at)} · last seen {timeAgo(s.last_seen_at)}
        </div>
      </div>
      <button
        type="button"
        onClick={onRevoke}
        data-testid={`session-revoke-${s.id}`}
        className="btn-ghost text-xs text-[var(--panini-red)]"
      >
        Revoke
      </button>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "unknown";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

// ============================================================================
// Events list
// ============================================================================
function EventsList({ events }) {
  if (!events) return <div className="gm-skel h-24 rounded-2xl" data-testid="events-loading" />;
  if (events.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-[var(--line)] bg-white p-4 text-sm text-[var(--smoke)]"
            data-testid="events-empty">
        No recent security activity.
      </div>
    );
  }
  return (
    <ol className="space-y-2" data-testid="events-list">
      {events.map((e, i) => (
        <li key={i} className="rounded-2xl border border-[var(--line)] bg-white p-3 flex items-center gap-3"
             data-testid={`event-${i}`}>
          <span className="w-8 h-8 rounded-full grid place-items-center bg-[var(--panini-yellow-soft)] border border-[var(--line)] shrink-0">
            <ShieldCheck size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[var(--ink)]">{e.label}</div>
            <div className="text-[11px] text-[var(--smoke)]">{timeAgo(e.at)}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ============================================================================
// Shared bits
// ============================================================================
function Section({ title, subtitle, children }) {
  return (
    <section className="grid md:grid-cols-[240px_1fr] gap-6">
      <div>
        <h3 className="font-display text-lg font-bold text-[var(--ink)]">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-[var(--smoke)]">{subtitle}</p>}
      </div>
      <div>{children}</div>
    </section>
  );
}

function PwField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">{label}</div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          data-testid={testid}
          autoComplete="new-password"
          className="gm-field pr-11"
        />
        <button type="button" onClick={onToggle}
                 className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] hover:text-[var(--ink)]"
                 aria-label={show ? "Hide password" : "Show password"}
                 data-testid={`${testid}-toggle`}>
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </label>
  );
}
